import { ApolloClient, InMemoryCache, HttpLink, from, ApolloLink, split } from "@apollo/client/core";
import type { NormalizedCacheObject, Operation } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import type { Client } from "graphql-ws";
import { ref } from "vue";

import { isMaintenanceResponse, markMaintenanceActive } from "@/core/api/maintenanceState";
import { getLogger } from "@/utils/logger";
import { recordRequestId } from "@/utils/requestIdTracker";
import { getSessionId, recordSessionIdFromResponse } from "@/utils/sessionIdTracker";
import { schemaMismatchLink } from "@/graphql/schemaMismatch";
import { createWsLiveness } from "@/graphql/wsLiveness";
import { buildWsClientOptions, WS_CONFIG, type WebSocketConnectionState } from "@/graphql/wsClientOptions";
import type { SupervisedTransport } from "@/graphql/superviseSubscription";

// Expand Apollo's production-stripped warnings into readable text in dev so
// "An error occurred! For more details, see https://go.apollo.dev/c/err#..." becomes the actual message.
if (import.meta.env.DEV) {
  void import("@apollo/client/dev").then(({ loadDevMessages, loadErrorMessages }) => {
    loadDevMessages();
    loadErrorMessages();
  });
}

const logger = getLogger("GraphQLClient");

/**
 * Apollo Client configuration for ToastHost GraphQL API.
 *
 * Features:
 * - HTTP link for queries/mutations
 * - WebSocket link for subscriptions (graphql-transport-ws protocol)
 * - Split link routing based on operation type
 * - Normalized caching with type policies
 * - Credentials included for cookie-based auth
 * - Automatic reconnection with exponential backoff
 * - Connection state tracking for UI feedback
 */

// =============================================================================
// CONNECTION STATE MANAGEMENT
// =============================================================================

// The last state the transport reported. A ref rather than a plain variable so
// a future consumer can watch it; today it is read through
// `getWsConnectionState`. The error text that comes with a transition is passed
// to the handlers rather than stored, because every consumer wants it at the
// moment it happens.
const _wsConnectionState = ref<WebSocketConnectionState>("disconnected");

// Connection event handlers for external subscription
type ConnectionHandler = (state: WebSocketConnectionState, error?: string) => void;
const connectionHandlers = new Set<ConnectionHandler>();

function notifyConnectionChange(state: WebSocketConnectionState, error?: string) {
  _wsConnectionState.value = state;
  connectionHandlers.forEach((handler) => handler(state, error));
}

export type { WebSocketConnectionState };

/** The shared socket's current state, for callers that need to read it rather
 * than react to a transition (a subscription that materializes while the socket
 * is already up gets no fresh callback). */
export function getWsConnectionState(): WebSocketConnectionState {
  return _wsConnectionState.value;
}

/** Register a handler for WebSocket connection-state changes. Returns an
 * unregister function. Used by the meeting subscription service to mirror the
 * old SSE connect/disconnect status. */
export function onWsConnectionChange(handler: ConnectionHandler): () => void {
  connectionHandlers.add(handler);
  return () => {
    connectionHandlers.delete(handler);
  };
}

// =============================================================================
// WEBSOCKET CONFIGURATION
// =============================================================================

// WebSocket URL - matches backend route at /graphql/ws
function getWsUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/graphql/ws`;
}

// Create graphql-ws client with reconnection logic
let wsClient: Client | null = null;

// Detects a socket that is dead but not closed, and reacts to the browser's
// network signal. graphql-ws pings but never times out the pong, so without
// this a half-open socket after a network change is invisible (#2937).
const wsLiveness = createWsLiveness({
  pongTimeoutMs: WS_CONFIG.pongTimeout,
  terminate: () => wsClient?.terminate(),
  log: (message) => logger.warn(`[GraphQL WS] ${message}`),
});

/**
 * Prod the transport into trying again now.
 *
 * `wsLiveness` already does this for itself on the browser's `online` event.
 * This is the same door for a caller that has other evidence the transport is
 * worth re-testing — the subscription supervisor calls it when a tab the user
 * abandoned becomes visible again, where sitting out the rest of a 45s backoff
 * is the whole of the delay the user sees. `replaceSocket` is for callers that
 * know the current socket cannot have survived; a visibility change does not.
 */
function nudgeWsConnection(options: { replaceSocket?: boolean } = {}): void {
  wsLiveness.nudge(options);
}

/**
 * The shared transport in the shape `superviseSubscription` needs, so a service
 * keeping a subscription alive wires up one object rather than three imports.
 */
export const wsTransport: SupervisedTransport = {
  onStateChange: onWsConnectionChange,
  currentState: getWsConnectionState,
  nudge: nudgeWsConnection,
};

function createWsClient(): Client {
  if (wsClient) {
    return wsClient;
  }

  wsClient = createClient(
    buildWsClientOptions({
      url: getWsUrl(),
      liveness: wsLiveness,
      getSessionId,
      onState: (state, error) => notifyConnectionChange(state, error),
      log: logger,
      verbose: import.meta.env.DEV,
    }),
  );

  return wsClient;
}

// Create WebSocket link
const wsLink = new GraphQLWsLink(createWsClient());

// =============================================================================
// HTTP LINK CONFIGURATION
// =============================================================================

const httpLink = new HttpLink({
  uri: "/graphql",
  credentials: "include", // Include cookies for authentication
});

// =============================================================================
// ERROR HANDLING LINK
// =============================================================================

// GraphQL error codes that represent expected failure modes — anonymous
// visitor hitting a protected query, session timeout, teardown race fetching
// a just-deleted meeting, etc. These flow through app-level handlers that
// recover gracefully, so we keep them at debug.
const EXPECTED_ERROR_CODES = new Set([
  "UNAUTHORIZED",
  "UNAUTHENTICATED",
  "CLUB_ASSOCIATION_REQUIRED",
  "FORBIDDEN",
  "NOT_FOUND",
]);

function allErrorsAreExpected(errors: readonly { extensions?: Record<string, unknown> }[]): boolean {
  if (errors.length === 0) return false;
  return errors.every((err) => {
    const code = err.extensions?.code;
    return typeof code === "string" && EXPECTED_ERROR_CODES.has(code);
  });
}

// Apollo link-level logging is deliberately quiet. Every GraphQL caller in
// the app (stores, services) catches the error and re-logs with semantic
// context (operation name, ids, what the UI was trying to do). Logging at
// `error` here meant every failure hit the log 2-6x. We now:
//   - expected codes  -> debug (same as before)
//   - everything else -> warn, so unexpected failures still stand out in
//     dev tools without polluting the ERROR stream the stores own.
const errorLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.errors && import.meta.env.DEV) {
      const reqId = readRequestIdFromOperation(operation);
      const suffix = reqId ? ` reqId=${reqId}` : "";
      if (allErrorsAreExpected(response.errors)) {
        logger.debug(`[GraphQL] Expected error${suffix}:`, response.errors);
      } else {
        logger.warn(`[GraphQL Errors${suffix}]`, response.errors);
      }
    }
    return response;
  });
});

/**
 * Latch the maintenance overlay when a GraphQL write is blocked (#2343). Apollo
 * writes go over `HttpLink`, not the axios `apiRequest` wrapper, so the 503 the
 * GraphQLController returns for mutations while maintenance is on surfaces here
 * as a `networkError` (a `ServerError` carrying `statusCode` + parsed `result`)
 * rather than through the REST detection path. Both transports emit the same
 * `maintenance: true` marker, so the shared helper decides.
 */
const maintenanceLink = onError(({ networkError }) => {
  // `networkError` is the correct accessor in Apollo Client 3.x; its @deprecated
  // tag is a forward notice about the 4.0 consolidation onto `error` (the type
  // def says "no action needed" for 3.x), and no `error` property exists yet.
  const serverError = networkError as { statusCode?: number; result?: unknown } | undefined;
  if (isMaintenanceResponse(serverError?.statusCode, serverError?.result)) {
    markMaintenanceActive();
  }
});

/**
 * Capture `X-Request-Id` off the GraphQL HTTP response so frontend errors
 * carry a grep target into `backend-dev.log`. Apollo exposes the response
 * via `operation.getContext().response` (set by HttpLink). Issue #1446.
 */
const requestIdLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const reqId = readRequestIdFromOperation(operation);
    if (reqId) {
      const ctx = operation.getContext();
      const url = typeof ctx?.uri === "string" ? ctx.uri : "/graphql";
      const status = ctx?.response?.status;
      recordRequestId(reqId, { url, status });
    }
    return response;
  });
});

/**
 * Send `X-Session-Id` on the way out and capture the echoed value on
 * the way in. The fetch interceptor in `installSessionIdInterceptor`
 * also covers GraphQL since Apollo's HttpLink uses fetch — this link
 * gives us a typed Apollo-aware path so the per-op log lines on the
 * server consistently get a sid even if a future HttpLink option
 * bypasses our fetch wrapper. Issue #1447.
 */
const sessionIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, "X-Session-Id": getSessionId() },
  }));
  return forward(operation).map((response) => {
    const ctx = operation.getContext();
    const headers = ctx?.response?.headers;
    const sid = typeof headers?.get === "function" ? headers.get("x-session-id") : null;
    if (sid) recordSessionIdFromResponse(sid);
    return response;
  });
});

function readRequestIdFromOperation(operation: Operation): string | null {
  const ctx = operation.getContext();
  const headers = ctx?.response?.headers;
  if (!headers) return null;
  // The browser fetch Headers API normalizes header names to lower-case.
  const id = typeof headers.get === "function" ? headers.get("x-request-id") : null;
  return id || null;
}

// =============================================================================
// LOGGING LINK
// =============================================================================

const loggingLink = new ApolloLink((operation, forward) => {
  if (import.meta.env.DEV) {
    const definition = getMainDefinition(operation.query);
    const operationType = definition.kind === "OperationDefinition" ? definition.operation : "unknown";

    logger.debug(`[GraphQL] ${operationType}: ${operation.operationName}`, {
      variables: operation.variables,
    });
  }
  return forward(operation);
});

// =============================================================================
// SPLIT LINK ROUTING
// =============================================================================

// Determine if an operation should use WebSocket (subscriptions) or HTTP (queries/mutations)
function isSubscriptionOperation(operation: Operation): boolean {
  const definition = getMainDefinition(operation.query);
  return definition.kind === "OperationDefinition" && definition.operation === "subscription";
}

// Split link: route subscriptions to WebSocket, everything else to HTTP
const splitLink = split(isSubscriptionOperation, wsLink, httpLink);

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

const cache = new InMemoryCache({
  typePolicies: {
    // Entity types with unique IDs
    User: {
      keyFields: ["id"],
    },
    // COT types - these use string IDs
    COTRound: {
      keyFields: ["id"],
    },
    COTSession: {
      keyFields: ["id"],
    },
    COTEventDetail: {
      keyFields: ["id"],
    },
    COTClubOfficerRecord: {
      keyFields: ["recordId"],
    },
    COTEventSummary: {
      keyFields: ["eventId"],
    },
    COTClubSummary: {
      keyFields: ["clubId"],
    },
    // Meeting types
    Meeting: {
      keyFields: ["id"],
      fields: {
        // `agenda` is a list of normalized AgendaItem refs. Without a merge
        // function Apollo warns on every refetch about "Cache data may be
        // lost when replacing the agenda field of a Meeting object". Because
        // AgendaItem is normalized by id, the incoming list already carries
        // the authoritative ordering and set — replace wholesale.
        agenda: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        // `projectRequests` shrinks when a request is approved/cancelled or
        // when the resolver hides the field for non-officers. Same rationale
        // as agenda — incoming list is authoritative.
        projectRequests: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    MeetingSummary: {
      keyFields: ["id"],
    },
    AgendaItem: {
      keyFields: ["id"],
      fields: {
        roleRequests: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        // `assignedUsers` shrinks when an assignment is removed; MeetingUser
        // is not normalized (keyFields: false) so Apollo cannot diff. Replace
        // wholesale — the incoming list is always authoritative.
        assignedUsers: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    RoleSignupRequest: {
      keyFields: ["id"],
    },
    VotingRoom: {
      keyFields: ["id"],
      fields: {
        // `candidates` shrinks when one is removed; incoming list is
        // authoritative. Same rationale as AgendaItem.roleRequests /
        // FeedbackSession.subjects.
        candidates: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    FeedbackSession: {
      keyFields: ["id"],
      fields: {
        // `subjects` and `feedback` are lists of normalized refs; when a
        // subject is removed the incoming list is shorter and Apollo warns
        // on replace. The incoming list is always the authoritative set.
        subjects: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        feedback: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    ToastmasterRole: {
      keyFields: ["id"],
    },
    ClubInfoSummary: {
      keyFields: ["id"],
    },
    // MeetingUser doesn't have a simple id, use combination
    MeetingUser: {
      keyFields: false, // MeetingUser has complex identity via meetingUserId
    },
    MeetingUserId: {
      keyFields: false, // Composite type
    },
    // Pathway types
    Pathway: {
      keyFields: ["id"],
    },
    PathwayProject: {
      keyFields: ["id"],
    },
    LevelRequirement: {
      keyFields: false, // No unique ID, nested in Pathway
    },
    UserPathwayEnrollment: {
      keyFields: ["pathwayId"], // Unique per user+pathway
    },
    PathwayCompletionRecord: {
      keyFields: ["id"],
    },
    MeetingParticipationRecord: {
      keyFields: ["id"],
    },
    SpeechTimings: {
      keyFields: false, // Embedded value object
    },
    // Result types (non-entities, no caching by ID)
    MeetingInitData: {
      keyFields: false, // Response wrapper type
    },
    MeetingUpdateEvent: {
      keyFields: false, // Subscription event type
    },
    COTSessionResult: {
      keyFields: false,
    },
    COTEventResult: {
      keyFields: false,
    },
    COTUploadResult: {
      keyFields: false,
    },
    COTDeleteResult: {
      keyFields: false,
    },
    COTOverrideResult: {
      keyFields: false,
    },
    COTRefreshResult: {
      keyFields: false,
    },
    Query: {
      fields: {
        // COT queries - cache by arguments
        cotRounds: {
          keyArgs: ["districtId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        cotEvents: {
          keyArgs: ["cotId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        cotSessions: {
          keyArgs: ["eventId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        cotClubSummaries: {
          keyArgs: ["districtId", "cotId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        // The marked half is a separate list (#5687), so it needs its own cache entry.
        cotUnmatchedAttendees: {
          keyArgs: ["cotId", "markedNotOfficer"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        // Meeting queries - cache by arguments
        meeting: {
          keyArgs: ["clubId", "meetingId"],
        },
        meetingInitData: {
          keyArgs: ["clubId", "meetingId", "includeMembers"],
        },
        // Pathway queries
        pathways: {
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        pathway: {
          keyArgs: ["pathwayId"],
        },
        myPathwayEnrollments: {
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        myPathwayCompletions: {
          keyArgs: ["pathwayId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        myParticipationRecords: {
          keyArgs: ["limit"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        // Project signup requests — refetches return the authoritative list
        // (officers see all, members see own). Replace wholesale; merging by
        // index would leave stale entries after cancellations.
        clubProjectRequests: {
          keyArgs: ["clubId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
        myClubProjectRequests: {
          keyArgs: ["clubId"],
          merge(_existing = [], incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

// =============================================================================
// APOLLO CLIENT INSTANCE
// =============================================================================

export const apolloClient = new ApolloClient<NormalizedCacheObject>({
  // maintenanceLink first so it observes networkErrors from every downstream
  // link (incl. the HTTP transport) and can latch the "back soon" overlay (#2343).
  link: from([maintenanceLink, schemaMismatchLink, loggingLink, sessionIdLink, requestIdLink, errorLink, splitLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "cache-first",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
  // Enable devtools in development
  devtools: {
    enabled: import.meta.env.DEV,
  },
});
